import React from 'react';
import cn from 'classnames';

import CheckboxInput from 'components/form/CheckboxInput.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import POS_READER_STATUS from 'constants/pos/posReaderStatus.const';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import noNilArray from 'utils/arrays/noNilArray.util';
import sortById from 'utils/arrays/sortById.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';

import CN from './CardReadersModal.module.scss';

const READER_STATUS = Object.freeze({
  online: POS_READER_STATUS.online,
  offline: POS_READER_STATUS.offline,
  ordered: POS_READER_STATUS.ordered,
});

const readerDisplayName = (reader, validReaders, t) => {
  const mainReaderLabel = t('composite.cardReaderModal.mainReader');
  const otherReaderLabel = t('composite.cardReaderModal.otherReader');

  const isMain = fromAnyToBool(reader?.isMain);
  const stylist = reader?.stylist;
  const name = reader?.name;

  if (name) return name;
  if (isMain && stylist) return `${mainReaderLabel} (${stylist})`;
  if (isMain || validReaders.length === 1) return mainReaderLabel;
  if (stylist) return t('composite.cardReaderModal.stylistReader', { name: stylist });
  return otherReaderLabel;
};

export const CardReadersList = ({ value, onSelect, isLoading, readers, generalReaderStatus, variant, isNonSlickSelected, onNonSlickSelect }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const sortedReaders = [...noNilArray(readers)].sort(sortById);
  const onlineReaders = sortedReaders.filter((reader) => READER_STATUS.online === reader.status);
  const hasNoOnlineReader = !onlineReaders.length;
  const isDepositInline = variant === 'depositInline';

  return (
    <div
      data-bem="CardReadersList"
      className={cn({
        [CN.component]: true,
        [CN[`${variant}Variant`]]: variant,
        [CN.dark]: isDarkMode,
      })}
      data-id={value?.id}
      data-reader-id={value?.readerId}
      data-reader-name={value?.name}
    >
      {isLoading ? (
        <OverlaySpinner />
      ) : (
        <div
          data-bem="CardReadersList__container"
          className={cn({
            [CN.container]: true,
            [CN.inline]: isDepositInline,
          })}
        >
          <div className={CN.title}> {!isDepositInline ? t('composite.cardReaderModal.chooseCardReader') : t('composite.cardReaderModal.chooseCardReaderDeposit')}</div>

          <div className={CN.readersList}>
            {sortedReaders.map((candidate) => {
              const { name, id, status, readerId } = candidate ?? {};

              const isChecked = value === candidate;
              const isDisabled = READER_STATUS.online !== status;
              const handleClick = isDisabled ? alwaysNoop : () => onSelect(candidate);

              return (
                <div
                  data-bem="CardReadersList__reader"
                  data-id={id}
                  data-reader-id={readerId}
                  data-reader-name={name}
                  data-reader-status={status}
                  key={id}
                  className={cn({
                    [CN.readerContainer]: true,
                    [CN.isSelected]: isChecked,
                    [CN.isDisabled]: isDisabled,
                  })}
                  onClick={handleClick}
                >
                  <div className={CN.readerItem}>
                    <div className={CN.leftGroup}>
                      <CheckboxInput key={id} variant="option" checked={isChecked} disabled={isDisabled} />

                      <div className={CN.readerName}>{readerDisplayName(candidate, onlineReaders, t)}</div>
                    </div>

                    <Badge variant={status === READER_STATUS.online ? 'success' : 'restricted'}>
                      <span className={CN.readerStatus}>
                        <Icon variant={status === READER_STATUS.online ? 'checkCircleSolid' : 'cloudOff'} width="md" />
                        {status}
                      </span>
                    </Badge>
                  </div>
                </div>
              );
            })}
            {/* Non-slick reader option is not part of the readers array as it is not a physical reader,
              but we want to show it as an option in the list in 2 of 3 places:
              Deposit panel - yes
              Client profile - add credit - yes,
              Checkout - not (it's there, hidden but not implemented for POS flow) */}

            <button
              type="button"
              data-bem="CardReadersList__non-slick"
              className={cn({
                [CN.readerContainer]: true,
                [CN.isSelected]: isNonSlickSelected,
              })}
              onClick={onNonSlickSelect}
            >
              <div className={CN.readerItem}>
                <div className={CN.leftGroup}>
                  <CheckboxInput variant="option" checked={isNonSlickSelected} />
                  <div className={CN.readerName}>{t('composite.cardReaderModal.nonSlickCardMachine')}</div>
                </div>
              </div>
            </button>
          </div>

          {POS_READER_STATUS.noReader === generalReaderStatus ||
            (hasNoOnlineReader && (
              <AlertPanel tag="CardReadersList__alert-no-readers" variant="alert">
                {t('composite.cardReaderModal.noReaders')}
              </AlertPanel>
            ))}
        </div>
      )}
    </div>
  );
};
